import React, { useEffect, useState } from "react";
import { motion } from "framer-motion";
import { api } from "../lib/api";
import { useApp } from "../context/AppContext";
import { Card, CardContent } from "./ui/card";
import { TrendingUp, Landmark, CreditCard, ChevronRight, ArrowUpRight, ArrowDownRight } from "lucide-react";
import CountUp from "./CountUp";

const fmt = (n) =>
    new Intl.NumberFormat("en-US", { style: "currency", currency: "USD", maximumFractionDigits: 0 }).format(n || 0);
const pct = (n) => (n === null || n === undefined ? "—" : `${n.toFixed(1)}%`);

/**
 * PortfolioAndDebts — a two-card row on the Dashboard showing:
 *   1. Investments summary (total value, top 3 holdings, unrealized gain/loss)
 *   2. Debts summary (credit balance, utilization %, student & mortgage totals)
 * Both auto-fetch; no button. Silently hides if the user has neither.
 */
const PortfolioAndDebts = () => {
    const { language } = useApp();
    const [inv, setInv] = useState(null);
    const [liab, setLiab] = useState(null);

    useEffect(() => {
        api.get("/investments").then((r) => setInv(r.data)).catch(() => setInv({ holdings: [], total_value: 0 }));
        api.get("/liabilities").then((r) => setLiab(r.data)).catch(() => setLiab(null));
    }, []);

    const hasInvestments = inv && inv.total_value > 0;
    const hasDebts = liab && (liab.total_credit_balance > 0 || (liab.student?.length || 0) > 0 || (liab.mortgage?.length || 0) > 0);
    if (!hasInvestments && !hasDebts) return null;

    return (
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6 mt-6" data-testid="portfolio-debts-row">
            {hasInvestments && (
                <motion.div
                    initial={{ opacity: 0, y: 12 }}
                    animate={{ opacity: 1, y: 0 }}
                    transition={{ duration: 0.5, ease: [0.22, 1, 0.36, 1] }}
                    data-testid="investments-widget"
                >
                    <Card className="rounded-3xl bg-[hsl(var(--card))] border-[hsl(var(--border))] shadow-sm h-full">
                        <CardContent className="p-6 md:p-7">
                            <div className="flex items-start justify-between mb-3">
                                <div>
                                    <div className="text-[11px] font-bold tracking-[0.24em] text-[hsl(var(--muted-foreground))] uppercase">
                                        {language === "es" ? "PORTAFOLIO · INVERSIONES" : "PORTFOLIO · INVESTMENTS"}
                                    </div>
                                    <div className="font-display font-bold text-2xl md:text-3xl tracking-tight mt-1">
                                        {language === "es" ? "Tus inversiones" : "Your investments"}
                                    </div>
                                </div>
                                <div className="w-10 h-10 rounded-2xl grid place-items-center bg-emerald-100 dark:bg-emerald-500/15 text-emerald-600 dark:text-emerald-400">
                                    <TrendingUp className="w-4 h-4" />
                                </div>
                            </div>
                            <div className="text-4xl md:text-5xl font-display font-extrabold tabular-nums tracking-tight text-emerald-600 dark:text-emerald-400">
                                <CountUp value={inv.total_value} format={fmt} />
                            </div>
                            {inv.gain_loss !== null && inv.gain_loss !== undefined && (
                                <div className="mt-1 flex items-center gap-1 text-sm">
                                    {inv.gain_loss >= 0 ? (
                                        <ArrowUpRight className="w-3.5 h-3.5 text-emerald-500" />
                                    ) : (
                                        <ArrowDownRight className="w-3.5 h-3.5 text-red-500" />
                                    )}
                                    <span className={inv.gain_loss >= 0 ? "text-emerald-500" : "text-red-500"}>
                                        {fmt(Math.abs(inv.gain_loss))} {language === "es" ? "vs costo" : "vs cost basis"}
                                    </span>
                                </div>
                            )}
                            <div className="mt-4 divide-y divide-[hsl(var(--border))]">
                                {inv.holdings.slice(0, 4).map((h, i) => (
                                    <div key={`${h.security_id || "x"}-${h.account_id || "y"}-${i}`} className="flex items-center justify-between py-2">
                                        <div className="min-w-0">
                                            <div className="font-medium truncate">{h.name}</div>
                                            <div className="text-xs text-[hsl(var(--muted-foreground))]">
                                                {h.ticker || h.type || ""}
                                                {h.quantity ? ` · ${h.quantity.toFixed(2)} ${language === "es" ? "unidades" : "units"}` : ""}
                                            </div>
                                        </div>
                                        <div className="font-semibold tabular-nums text-right">{fmt(h.institution_value)}</div>
                                    </div>
                                ))}
                            </div>
                        </CardContent>
                    </Card>
                </motion.div>
            )}

            {hasDebts && (
                <motion.div
                    initial={{ opacity: 0, y: 12 }}
                    animate={{ opacity: 1, y: 0 }}
                    transition={{ duration: 0.5, ease: [0.22, 1, 0.36, 1], delay: 0.06 }}
                    data-testid="debts-widget"
                >
                    <Card className="rounded-3xl bg-[hsl(var(--card))] border-[hsl(var(--border))] shadow-sm h-full">
                        <CardContent className="p-6 md:p-7">
                            <div className="flex items-start justify-between mb-3">
                                <div>
                                    <div className="text-[11px] font-bold tracking-[0.24em] text-[hsl(var(--muted-foreground))] uppercase">
                                        {language === "es" ? "PASIVOS · DEUDAS" : "LIABILITIES · DEBTS"}
                                    </div>
                                    <div className="font-display font-bold text-2xl md:text-3xl tracking-tight mt-1">
                                        {language === "es" ? "Tus deudas" : "Your debts"}
                                    </div>
                                </div>
                                <div className="w-10 h-10 rounded-2xl grid place-items-center bg-[#A75A22]/15 dark:bg-[#F9C77E]/15 text-[#A75A22] dark:text-[#F9C77E]">
                                    <Landmark className="w-4 h-4" />
                                </div>
                            </div>

                            <div className="grid grid-cols-2 gap-4 mb-4">
                                <div>
                                    <div className="text-[10px] font-bold tracking-[0.2em] text-[hsl(var(--muted-foreground))] uppercase">
                                        {language === "es" ? "Saldo de tarjetas" : "Card balance"}
                                    </div>
                                    <div className="font-display font-extrabold text-2xl md:text-3xl tabular-nums text-[#A75A22] dark:text-[#F9C77E]">
                                        <CountUp value={liab.total_credit_balance || 0} format={fmt} />
                                    </div>
                                </div>
                                <div>
                                    <div className="text-[10px] font-bold tracking-[0.2em] text-[hsl(var(--muted-foreground))] uppercase">
                                        {language === "es" ? "Utilización" : "Utilization"}
                                    </div>
                                    <div className={`font-display font-extrabold text-2xl md:text-3xl tabular-nums ${(liab.credit_utilization_pct || 0) < 30 ? "text-emerald-600 dark:text-emerald-400" : (liab.credit_utilization_pct || 0) < 70 ? "text-[#F9C77E]" : "text-red-500"}`}>
                                        {liab.credit_utilization_pct !== null && liab.credit_utilization_pct !== undefined ? (
                                            <CountUp value={liab.credit_utilization_pct} format={pct} />
                                        ) : "—"}
                                    </div>
                                </div>
                            </div>

                            <div className="divide-y divide-[hsl(var(--border))]">
                                {(liab.credit || []).map((c, i) => (
                                    <div key={"c" + i} className="flex items-center justify-between py-2">
                                        <div className="flex items-center gap-2 min-w-0">
                                            <CreditCard className="w-3.5 h-3.5 text-[hsl(var(--muted-foreground))]" />
                                            <div className="text-sm truncate">
                                                {language === "es" ? "Tarjeta de crédito" : "Credit card"}
                                                {c.next_payment_due_date ? ` · ${language === "es" ? "vence" : "due"} ${String(c.next_payment_due_date).slice(0, 10)}` : ""}
                                            </div>
                                        </div>
                                        <div className="text-sm font-semibold tabular-nums">{fmt(c.last_statement_balance || 0)}</div>
                                    </div>
                                ))}
                                {(liab.student || []).map((s, i) => (
                                    <div key={"s" + i} className="flex items-center justify-between py-2">
                                        <div className="flex items-center gap-2 min-w-0">
                                            <Landmark className="w-3.5 h-3.5 text-[hsl(var(--muted-foreground))]" />
                                            <div className="text-sm truncate">
                                                {language === "es" ? "Préstamo estudiantil" : "Student loan"}
                                                {s.interest_rate_percentage ? ` · ${s.interest_rate_percentage}% APR` : ""}
                                            </div>
                                        </div>
                                        <div className="text-sm font-semibold tabular-nums">{fmt(s.last_statement_balance || s.origination_principal_amount || 0)}</div>
                                    </div>
                                ))}
                                {(liab.mortgage || []).map((m, i) => (
                                    <div key={"m" + i} className="flex items-center justify-between py-2">
                                        <div className="flex items-center gap-2 min-w-0">
                                            <Landmark className="w-3.5 h-3.5 text-[hsl(var(--muted-foreground))]" />
                                            <div className="text-sm truncate">
                                                {language === "es" ? "Hipoteca" : "Mortgage"}
                                                {m.interest_rate?.percentage ? ` · ${m.interest_rate.percentage}% APR` : ""}
                                            </div>
                                        </div>
                                        <div className="text-sm font-semibold tabular-nums">{fmt(m.last_payment_amount || 0)}/mo</div>
                                    </div>
                                ))}
                            </div>
                        </CardContent>
                    </Card>
                </motion.div>
            )}
        </div>
    );
};

export default PortfolioAndDebts;
